import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import {
  buildCumulativeData, flattenForSnapshot, resolveDraft, buildForecastStructure, normalizeDecisions,
  getOrCreateDraft, saveDraft, commitForecastDraft, createRevisionFrom, discardDraft,
  loadForecastVersions,
} from '../lib/buildCostControlData'

const fmt = n => n == null ? '—' : Math.round(n).toLocaleString()
const fmtN = n => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function monthYearLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return `${MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

// ─── Forecast Modal ────────────────────────────────────────────────────────────
// Unchanged from Phase 1 in spirit: never writes to the database directly.
// It only reports the calculated eac/etc/method/inputs back via
// onDraftChange(), staged in the parent's local draftEdits — which are then
// persisted to the actual Draft row via saveDraft() (a separate, explicit
// action), and only become permanent via commitForecastDraft() (Commit).
function ForecastModal({ row, draft, readOnly, onClose, onDraftChange, onClearDraft }) {
  const [method, setMethod] = useState(draft?.method || row.savedEac?.method || (row.level === 'resource' ? 'budget' : 'commitment'))
  const [manual, setManual] = useState(draft?.manual_amount != null ? String(draft.manual_amount) : (row.savedEac?.manual_amount != null ? String(row.savedEac.manual_amount) : ''))
  const [qty,    setQty]    = useState(draft?.qty != null ? String(draft.qty) : (row.savedEac?.qty != null ? String(row.savedEac.qty) : ''))
  const [rate,   setRate]   = useState(draft?.rate != null ? String(draft.rate) : (row.savedEac?.rate != null ? String(row.savedEac.rate) : ''))

  const budget     = parseFloat(row.budget)     || 0
  const commitment = parseFloat(row.commitment) || 0
  const actual     = parseFloat(row.actual)     || 0

  const eac = useMemo(() => {
    if (method === 'budget')     return budget
    if (method === 'commitment') return commitment
    if (method === 'actual')     return actual
    if (method === 'manual')     return parseFloat(manual) || 0
    if (method === 'qty_rate')   return (parseFloat(qty)||0) * (parseFloat(rate)||0)
    return 0
  }, [method, manual, qty, rate, budget, commitment, actual])

  const etc = eac - actual

  function stageDraft() {
    const rowKey = row.sub_code || row.resource_code || row.cost_code
    onDraftChange(rowKey, {
      eac_amount: eac,
      method,
      manual_amount: method === 'manual' ? (parseFloat(manual) || null) : null,
      qty: method === 'qty_rate' ? (parseFloat(qty) || null) : null,
      rate: method === 'qty_rate' ? (parseFloat(rate) || null) : null,
    })
    onClose()
  }

  function clearDraft() {
    const rowKey = row.sub_code || row.resource_code || row.cost_code
    onClearDraft(rowKey)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <div>
            <h2 className="text-base font-bold text-[#1C1A18]">Forecast EAC {readOnly && <span className="text-xs font-normal text-[#9CA3AF]">(read-only — committed revision)</span>}</h2>
            <p className="text-xs text-[#9CA3AF] mt-0.5">
              {row.cost_code && <span className="font-mono text-[#1A7A4A] mr-2">{row.cost_code}</span>}
              {row.sub_code  && <span className="font-mono text-[#6B7280] mr-2">{row.sub_code}</span>}
              {row.description}
            </p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {[{l:'Budget',v:budget},{l:'Commitment',v:commitment},{l:'Actual',v:actual}].map(k => (
              <div key={k.l} className="bg-white rounded-xl px-3 py-2.5 text-center">
                <div className="text-xs text-[#9CA3AF]">{k.l}</div>
                <div className="font-bold text-sm text-[#1C1A18]">{fmtN(k.v)}</div>
              </div>
            ))}
          </div>

          {!readOnly && (
            <>
              <div>
                <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Method</label>
                <div className={`grid gap-2 ${row.level === 'resource' ? 'grid-cols-3' : 'grid-cols-4'}`}>
                  {(row.level === 'resource' ? [
                    {v:'budget',l:'Budget'},
                    {v:'manual',l:'Manual'},
                    {v:'qty_rate',l:'Qty × Rate'},
                  ] : [
                    {v:'commitment',l:'Commitment'},
                    {v:'actual',l:'Actual'},
                    {v:'manual',l:'Manual'},
                    {v:'qty_rate',l:'Qty × Rate'},
                  ]).map(m => (
                    <button key={m.v} onClick={() => setMethod(m.v)}
                      className={`py-2 rounded-lg text-xs font-medium border transition-colors ${method===m.v ? 'bg-amber-500 text-white border-amber-500' : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                      {m.l}
                    </button>
                  ))}
                </div>
                {row.level === 'resource' && (
                  <p className="text-[10.5px] text-[#9CA3AF] mt-2">
                    ⚠ تحديد Forecast مباشرة هنا (بأي Method) بيوقف التجميع التلقائي من الـ Sub Codes تحت هذا الـ Resource Code — الرقم اللي هتحفظه هنا هيبقى هو الـ EAC النهائي بدل مجموعهم.
                  </p>
                )}
              </div>

              {method === 'manual' && (
                <div>
                  <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Amount</label>
                  <input type="number" placeholder="Enter EAC amount" value={manual}
                    onChange={e => setManual(e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
                </div>
              )}
              {method === 'qty_rate' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Qty</label>
                    <input type="number" placeholder="Quantity" value={qty}
                      onChange={e => setQty(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Rate</label>
                    <input type="number" placeholder="Rate" value={rate}
                      onChange={e => setRate(e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-col items-center justify-center">
              <span className="text-xs text-amber-600 font-semibold uppercase tracking-wide">EAC {readOnly ? '' : 'Preview'}</span>
              <span className="text-lg font-black text-amber-700">{fmtN(eac)}</span>
            </div>
            <div className="bg-[#F3F4F6] border border-[#E5E7EB] rounded-xl px-4 py-3 flex flex-col items-center justify-center">
              <span className="text-xs text-[#6B7280] font-semibold uppercase tracking-wide">ETC (EAC − Actual)</span>
              <span className="text-lg font-black text-[#374151]">{fmtN(etc)}</span>
            </div>
          </div>
        </div>

        {!readOnly && (
          <div className="px-5 pb-2">
            <p className="text-xs text-[#9CA3AF]">This edit is staged locally — press <b>Save Draft</b> in the toolbar to persist it, or <b>Commit</b> to make the whole Draft a permanent Revision.</p>
          </div>
        )}
        <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
          <button onClick={onClose} className="py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && draft && (
            <button onClick={clearDraft} className="py-2 px-4 border border-red-200 text-red-500 rounded-lg text-sm hover:bg-red-50">
              Clear Change
            </button>
          )}
          {!readOnly && (
            <button onClick={stageDraft} className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600">
              Stage Change
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Commit Modal — collects an optional note, confirms the irreversible
// action, then calls commitForecastDraft(). ────────────────────────────────
function CommitModal({ periodLabel, onClose, onConfirm, committing }) {
  const [notes, setNotes] = useState('')
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-[#E5E7EB]">
          <h2 className="text-base font-bold text-[#1C1A18]">Commit Forecast — {periodLabel}</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">This creates a new, permanent, immutable Revision. It can never be edited afterward — only reviewed, compared, or used as the basis for a new Revision.</p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            placeholder="e.g. Initial forecast for this cycle"
            className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"/>
        </div>
        <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
          <button onClick={onClose} disabled={committing} className="py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA] disabled:opacity-50">Cancel</button>
          <button onClick={() => onConfirm(notes)} disabled={committing}
            className="flex-1 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 disabled:opacity-50">
            {committing ? 'Committing…' : '🔒 Commit Revision'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Create Revision Modal — picks the new period, confirms the source
// revision it will be seeded from. If an active Draft already exists
// (ADD_Forecast_Architecture.md SS13 Invariant #1: at most one Draft per
// project+source), this modal surfaces an explicit choice to the user
// BEFORE calling create_revision_from() — Option C, agreed: a Draft
// represents unfinished user work and must never be silently reused or
// discarded. The SQL function itself is unchanged; it still rejects the
// call if a Draft exists. This modal simply avoids ever making that call
// without the user's explicit direction, and offers discardDraft() as an
// explicit, confirmed action when the user chooses it. ──────────────────
function CreateRevisionModal({ sourceLabel, existingDraftInfo, onClose, onConfirm, onDiscardAndCreate, onContinueExistingDraft, creating }) {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())

  // Conflict step: an active Draft already exists. Present the three
  // choices explicitly — Continue existing Draft / Discard and create new
  // / Cancel — rather than attempting create_revision_from() and letting
  // the user hit a raw database error.
  if (existingDraftInfo) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md">
          <div className="p-5 border-b border-[#E5E7EB]">
            <h2 className="text-base font-bold text-[#1C1A18]">An active Draft already exists</h2>
            <p className="text-xs text-[#6B7280] mt-2">
              A Draft ({existingDraftInfo.forecast_date || 'in progress'}) already exists for this project. A Draft
              represents unfinished work and is never silently discarded or reused for a different Revision lineage.
              Choose how to proceed:
            </p>
          </div>
          <div className="p-5 space-y-3">
            <button onClick={onContinueExistingDraft} disabled={creating}
              className="w-full py-2.5 px-4 border border-[#E5E7EB] rounded-lg text-sm text-left hover:bg-[#FAFAFA] disabled:opacity-50">
              <div className="font-semibold text-[#1C1A18]">Continue existing Draft</div>
              <div className="text-xs text-[#9CA3AF] mt-0.5">Close this dialog and keep working on the current Draft.</div>
            </button>
            <button onClick={onDiscardAndCreate} disabled={creating}
              className="w-full py-2.5 px-4 border border-red-200 rounded-lg text-sm text-left hover:bg-red-50 disabled:opacity-50">
              <div className="font-semibold text-red-700">Discard existing Draft and create new</div>
              <div className="text-xs text-[#9CA3AF] mt-0.5">Permanently discards the current Draft (including any saved content), then creates a new Draft seeded from <b>{sourceLabel}</b>.</div>
            </button>
          </div>
          <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
            <button onClick={onClose} disabled={creating} className="flex-1 py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA] disabled:opacity-50">Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-[#E5E7EB]">
          <h2 className="text-base font-bold text-[#1C1A18]">Create Revision</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">
            A new editable Draft will be created, seeded from <b>{sourceLabel}</b>. {sourceLabel} itself remains permanent and unchanged forever.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide">New Draft's Forecast Period</label>
          <div className="grid grid-cols-2 gap-3">
            <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
              className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
              {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || year)}
              className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
          <button onClick={onClose} disabled={creating} className="py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA] disabled:opacity-50">Cancel</button>
          <button onClick={() => onConfirm(month, year)} disabled={creating}
            className="flex-1 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 disabled:opacity-50">
            {creating ? 'Creating…' : '✏️ Create Revision'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function DirectForecast({ projectId }) {
  const { can } = useProjectAuth(projectId)
  const [costCodes,    setCostCodes]    = useState([])
  const [budgetByResource, setBudgetByResource] = useState({})
  const [commitByCC,   setCommitByCC]   = useState({})
  const [actualByCC,   setActualByCC]   = useState({})
  const [loading,      setLoading]      = useState(false)
  const [busy,         setBusy]         = useState(false)
  const [forecastModal,setForecastModal]= useState(null)
  const [expandedCC,   setExpandedCC]   = useState(new Set())
  const [expandedRes,  setExpandedRes]  = useState(new Set())

  const [draftId, setDraftId] = useState(null)
  const [draftForecastDate, setDraftForecastDate] = useState(null)
  const [revisions, setRevisions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState('draft')
  const [showCommitModal, setShowCommitModal] = useState(false)
  const [showRevisionModal, setShowRevisionModal] = useState(false)

  const [draftEdits, setDraftEdits] = useState({})
  const hasUnsavedEdits = Object.keys(draftEdits).length > 0
  const [draftPersistedRows, setDraftPersistedRows] = useState({})
  // Baseline — read-only reference to the source Revision (based_on_snapshot_id),
  // never copied into the Draft's own storage. See load()'s Baseline section
  // and activeData's Draft-mode eac() for how this is used purely as a
  // fallback VALUE, never as an ownership signal.
  const [baselineRows, setBaselineRows] = useState({})

  const toggleCC = (code) => setExpandedCC(s => { const n=new Set(s); n.has(code)?n.delete(code):n.add(code); return n })
  const toggleRes = (code) => setExpandedRes(s => { const n=new Set(s); n.has(code)?n.delete(code):n.add(code); return n })

  function handleDraftChange(rowKey, values) {
    setDraftEdits(prev => ({ ...prev, [rowKey]: values }))
  }
  function handleClearDraft(rowKey) {
    setDraftEdits(prev => { const n = { ...prev }; delete n[rowKey]; return n })
  }

  useEffect(() => {
    function handler(e) {
      if (!hasUnsavedEdits) return
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedEdits])

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)

    const { data: cc } = await supabase.from('cost_codes')
      .select('id, code, description, level, parent_id, cost_type, resource_dict_code')
      .eq('project_id', projectId)
      .eq('is_active', true).order('code')
    function findTopLevelCostType(node, all) {
      if (parseInt(node.level) === 1) return node.cost_type
      let cur = node
      let hops = 0
      while (cur.parent_id && hops < 5) {
        const parent = all.find(p => p.id === cur.parent_id)
        if (!parent) return null
        if (parseInt(parent.level) === 1) return parent.cost_type
        cur = parent
        hops++
      }
      return null
    }
    const directCC = (cc||[]).filter(c => findTopLevelCostType(c, cc||[]) === 'direct')
    setCostCodes(directCC)

    const cumulative = await buildCumulativeData(projectId)
    const bResource = {}
    const commitBySubCode = {}
    const actualBySubCode = {}
    for (const parent of (cumulative.directRows || [])) {
      for (const res of (parent.resourceRows || [])) {
        const key = String(res.code || '').trim().toLowerCase()
        bResource[key] = res.bac || 0
        commitBySubCode[res.code] = res.commitment || 0
        actualBySubCode[res.code] = res.totalActualCost || 0
      }
      for (const sub of (parent.children || [])) {
        commitBySubCode[sub.code] = sub.commitment || 0
        actualBySubCode[sub.code] = sub.totalActualCost || 0
      }
    }
    setBudgetByResource(bResource)
    setCommitByCC(commitBySubCode)
    setActualByCC(actualBySubCode)

    const now = new Date()
    const newDraftId = await getOrCreateDraft(projectId, 'direct', now.getMonth() + 1, now.getFullYear())
    setDraftId(newDraftId)

    const { data: draftRow } = await supabase.from('forecast_snapshots')
      .select('forecast_date, based_on_snapshot_id').eq('id', newDraftId).single()
    setDraftForecastDate(draftRow?.forecast_date || null)

    // ─── Baseline (ADD_Forecast_Architecture.md domain-modeling addendum,
    // this conversation) — a READ-ONLY REFERENCE to the source Revision via
    // based_on_snapshot_id, never a copied dataset. Loaded into its own
    // state, entirely separate from draftPersistedRows. This is what lets
    // activeData distinguish "a real decision in the current Draft"
    // (draftPersistedRows) from "a starting-point value inherited from the
    // Baseline" (baselineRows) — the Baseline is read as a fallback VALUE
    // only, never as an ownership signal (hasEntry() never checks it).
    // If based_on_snapshot_id is null (a Draft not created via Create
    // Revision — e.g. the project's very first Draft), baselineRows stays
    // empty and every row falls through to the plain Budget fallback,
    // exactly as before Baselines existed.
    let baseline = {}
    if (draftRow?.based_on_snapshot_id) {
      const { data: baselineHistoryRows } = await supabase.from('forecast_history')
        .select('row_key, eac_amount, etc_amount, method, manual_amount, qty, rate, is_forecasted')
        .eq('snapshot_id', draftRow.based_on_snapshot_id)

      // Decision Layer flag resolution for pre-migration source Revisions
      // (ADD_Forecast_Architecture.md Addendum SS14) — mirrors
      // loadForecastHistory()'s resolveIsForecasted() exactly. A Revision
      // committed BEFORE the is_forecasted column existed has
      // is_forecasted=false on every row by default (the column's own
      // DEFAULT); the real historical classification for those rows lives
      // in the separate forecast_history_decision_flags table.
      const { data: flagData } = await supabase
        .from('forecast_history_decision_flags')
        .select('row_key, is_forecasted')
        .eq('snapshot_id', draftRow.based_on_snapshot_id)
      const flagByRowKey = {}
      for (const f of (flagData || [])) flagByRowKey[f.row_key] = f.is_forecasted

      // CRITICAL: the Baseline must only contain rows that represented a
      // REAL forecast decision in the source Revision (is_forecasted=true,
      // resolved via the flag table for pre-migration Revisions, else the
      // column directly). Rows that were merely a frozen Budget-fallback in
      // the source Revision (is_forecasted=false) carry no real
      // information — including a value of 0 in eac_amount — and must NOT
      // be treated as an Effective Forecast here. Omitting them from
      // baselineRows entirely is what lets resolveDraft()'s leaf fallback
      // correctly fall through to this Draft's own live Budget for those
      // rows, instead of incorrectly reporting hasEffectiveForecast=true
      // with a stale/zero value that then incorrectly makes every ancestor
      // Resource/Cost Code believe "something is forecasted here" and stop
      // aggregating toward Budget.
      for (const r of (baselineHistoryRows || [])) {
        const resolvedIsForecasted = flagByRowKey[r.row_key] !== undefined
          ? flagByRowKey[r.row_key]
          : (r.is_forecasted === true)
        if (!resolvedIsForecasted) continue
        baseline[r.row_key] = { eac_amount: r.eac_amount, method: r.method, manual_amount: r.manual_amount, qty: r.qty, rate: r.rate }
      }
    }
    setBaselineRows(baseline)

    // draftPersistedRows now contains ONLY rows physically saved to THIS
    // Draft's own forecast_history (snapshot_id = newDraftId) — under the
    // new architecture (Migration 8), create_revision_from() no longer
    // copies any rows here, so this naturally contains only genuine
    // Current Draft Decisions from the moment of creation onward. (A Draft
    // created before Migration 8, under the old copy-based approach, may
    // still have inherited rows physically present here — that is a known,
    // accepted state for pre-existing Drafts, not something this load()
    // logic tries to clean up.)
    const { data: draftHistoryRows } = await supabase.from('forecast_history')
      .select('row_key, eac_amount, etc_amount, method, manual_amount, qty, rate')
      .eq('snapshot_id', newDraftId)
    const persisted = {}
    for (const r of (draftHistoryRows || [])) {
      persisted[r.row_key] = { eac_amount: r.eac_amount, method: r.method, manual_amount: r.manual_amount, qty: r.qty, rate: r.rate }
    }
    setDraftPersistedRows(persisted)
    setDraftEdits({})

    const { revisions: revList } = await loadForecastVersions(projectId, 'direct')
    setRevisions(revList)

    setLoading(false)
  }, [projectId])

  useEffect(() => { load() }, [load])

  const tree = useMemo(() => {
    const parents   = costCodes.filter(c => parseInt(c.level) === 1)
    const resources = costCodes.filter(c => parseInt(c.level) === 4)
    const subs      = costCodes.filter(c => parseInt(c.level) === 2)
    return parents.map(p => {
      const resChildren = resources.filter(r => r.parent_id === p.id)
      const orphanSubs  = subs.filter(s => s.parent_id === p.id)
      const resourcesWithSubs = resChildren.map(r => {
        const rSubs = subs.filter(s => s.parent_id === r.id)
        const dictKey = String(r.resource_dict_code || r.code).trim().toLowerCase()
        return { ...r, subs: rSubs, budget: budgetByResource[dictKey] || 0 }
      })
      const budget = resourcesWithSubs.reduce((s,r) => s + r.budget, 0)
      return { ...p, resources: resourcesWithSubs, orphanSubs, budget }
    })
  }, [costCodes, budgetByResource])

  const viewingDraft = selectedVersion === 'draft'
  const selectedRevision = viewingDraft ? null : revisions.find(r => r.id === selectedVersion)

  // ─── resolveDraft() consumption (ADD_Forecast_Architecture.md SS15) ────
  // This is now the ONLY place Forecast (EAC) ownership/aggregation is
  // decided in Draft mode — the same pure resolveDraft() function
  // flattenForSnapshot() calls at Commit. No independent ownership logic
  // remains in this component; activeData below simply reads this result.
  //
  // forecastStructure comes from buildForecastStructure() — the raw
  // domain hierarchy (Cost Codes/Resources/Sub Codes + Budget/Actual/
  // Commitment, no Forecast resolution baked in). This replaces an earlier
  // adapter that converted this screen's own render tree (`tree`) into
  // resolveDraft()'s expected shape — that adapter was a symptom fix; the
  // real root cause (buildCumulativeData() returning an already-resolved
  // financial model, contaminating resolveDraft()'s ownership logic with
  // its own internal ownership resolution) is now fixed at the source via
  // buildForecastStructure(), which both this screen and
  // flattenForSnapshot() call identically — one raw structure, one
  // resolution engine, no adapters, no divergent hierarchies.
  const [forecastStructure, setForecastStructure] = useState([])
  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    ;(async () => {
      const structure = await buildForecastStructure(projectId, 'direct')
      if (!cancelled) setForecastStructure(structure)
    })()
    return () => { cancelled = true }
  }, [projectId, actualByCC, commitByCC]) // re-fetch whenever live SSOT data reloads

  const resolvedTree = useMemo(() => {
    if (!viewingDraft) return []
    const merged = { ...draftPersistedRows, ...draftEdits }
    return resolveDraft(forecastStructure, baselineRows, merged)
  }, [viewingDraft, forecastStructure, baselineRows, draftPersistedRows, draftEdits])

  // Flat lookup by row_key (code) — { eac, isOwner } for every node
  // resolveDraft() resolved (Resources, their Sub Codes, and orphan Sub
  // Codes, and Cost Codes themselves).
  const resolvedByCode = useMemo(() => {
    const map = {}
    for (const parent of resolvedTree) {
      for (const res of (parent.resourceRows || [])) {
        map[res.code] = { eac: res.resolvedEac || 0, isOwner: res.isOwner === true }
        for (const child of (res.children || [])) {
          map[child.code] = { eac: child.resolvedEac || 0, isOwner: child.isOwner === true }
        }
      }
      for (const child of (parent.children || [])) {
        map[child.code] = { eac: child.resolvedEac || 0, isOwner: child.isOwner === true }
      }
      map[parent.code] = { eac: parent.resolvedEac || 0, isOwner: parent.isOwner === true }
    }
    return map
  }, [resolvedTree])

  // ─── Single Active Data Provider ──────────────────────────────────────────
  // This is the ONE switch between Live mode (Draft) and Snapshot mode
  // (Revision) in the whole component. Every display function below reads
  // exclusively from `activeData` — none of them branch on viewingDraft or
  // selectedVersion themselves anymore. This is what guarantees a Revision
  // is a TRUE immutable snapshot: Budget/Actual/Commitment/EAC for a
  // Revision all come from the SAME frozen forecast_history row as its EAC
  // already did, instead of Actual/Commitment/Budget silently drifting in
  // from current live SSOT state while only EAC stayed frozen.
  //
  //   - Draft  → activeData.eac()/hasEntry() read directly from
  //              resolvedByCode (resolveDraft()'s output — SS15). Actual/
  //              Commitment still come from the live SSOT maps
  //              (commitByCC/actualByCC, populated by buildCumulativeData()
  //              in load()), per SS15.3: ownership never affects these,
  //              they are pure aggregations regardless of Forecast state.
  //   - Revision → activeData wraps ONE flat map loaded once from
  //              forecast_history for that snapshot_id, keyed by row_key,
  //              carrying eac/actual/budget/commitment together — the exact
  //              frozen values committed at that moment, nothing live mixed in.
  const [revisionSnapshotRows, setRevisionSnapshotRows] = useState({}) // { row_key: { eac, actual, budget, commitment, is_forecasted } }
  useEffect(() => {
    if (viewingDraft || !selectedVersion) { setRevisionSnapshotRows({}); return }
    let cancelled = false
    ;(async () => {
      const { data } = await supabase.from('forecast_history')
        .select('row_key, eac_amount, snapshot_actual, snapshot_budget, snapshot_commitment, is_forecasted')
        .eq('snapshot_id', selectedVersion)
      if (cancelled) return

      // Decision Layer flag resolution for pre-migration rows
      // (ADD_Forecast_Architecture.md Addendum SS14) — mirrors
      // loadForecastHistory()'s resolveIsForecasted() exactly. Rows
      // committed BEFORE the is_forecasted column existed still have
      // is_forecasted=false on the column itself (the column's own
      // DEFAULT); their real historical classification lives only in the
      // separate, append-only forecast_history_decision_flags table. This
      // screen queries forecast_history directly (not via
      // loadForecastHistory()), so it needs the same fallback lookup here.
      const { data: flagData } = await supabase
        .from('forecast_history_decision_flags')
        .select('row_key, is_forecasted')
        .eq('snapshot_id', selectedVersion)
      if (cancelled) return
      const flagByRowKey = {}
      for (const f of (flagData || [])) flagByRowKey[f.row_key] = f.is_forecasted

      const rows = {}
      for (const r of (data || [])) {
        const resolvedIsForecasted = flagByRowKey[r.row_key] !== undefined
          ? flagByRowKey[r.row_key]
          : (r.is_forecasted === true)
        rows[r.row_key] = {
          eac: parseFloat(r.eac_amount) || 0,
          actual: parseFloat(r.snapshot_actual) || 0,
          budget: parseFloat(r.snapshot_budget) || 0,
          commitment: parseFloat(r.snapshot_commitment) || 0,
          is_forecasted: resolvedIsForecasted,
        }
      }
      setRevisionSnapshotRows(rows)
    })()
    return () => { cancelled = true }
  }, [viewingDraft, selectedVersion])

  const activeData = useMemo(() => {
    if (viewingDraft) {
      return {
        mode: 'draft',
        // hasEntry()/eac() now read directly from resolvedByCode —
        // resolveDraft()'s output — instead of duplicating ownership logic
        // here. This is the fix for SS15's discovered duplication: there is
        // exactly one place Forecast ownership/aggregation is computed.
        hasEntry: (rowKey) => resolvedByCode[rowKey]?.isOwner === true,
        eac: (rowKey) => resolvedByCode[rowKey]?.eac || 0,
        // Actual/Commitment always come from the live SSOT
        // (buildCumulativeData(), loaded into commitByCC/actualByCC by
        // load()) — correct here, since a Draft is explicitly NOT frozen
        // yet; it should reflect current project state until committed.
        // Per SS15.3, ownership never influences these — pure aggregation.
        actual: (rowKey) => actualByCC[rowKey] || 0,
        commitment: (rowKey) => commitByCC[rowKey] || 0,
      }
    }
    // Revision mode: EVERY value comes from the one frozen snapshot map —
    // no live SSOT data is read here at all.
    //
    // hasEntry() (ADD_Forecast_Architecture.md SS4/SS5/SS6) reads
    // is_forecasted, NOT row presence. Every forecastable node has a
    // Snapshot Layer row in a committed Revision (Philosophy A — complete,
    // self-contained statement), including nodes that were never actually
    // forecasted (their frozen eac_amount is a Budget-fallback value).
    return {
      mode: 'revision',
      hasEntry: (rowKey) => revisionSnapshotRows[rowKey]?.is_forecasted === true,
      eac: (rowKey) => revisionSnapshotRows[rowKey]?.eac || 0,
      actual: (rowKey) => revisionSnapshotRows[rowKey]?.actual || 0,
      commitment: (rowKey) => revisionSnapshotRows[rowKey]?.commitment || 0,
    }
  }, [viewingDraft, resolvedByCode, actualByCC, commitByCC, revisionSnapshotRows])

  // These now simply read the already-fully-resolved value from
  // activeData — in Draft mode, activeData.eac()/hasEntry() delegate
  // directly to resolveDraft()'s output (SS15), which has already applied
  // ownership + aggregation for every node in the tree. No aggregation or
  // ownership branching happens here anymore — that would be exactly the
  // duplicated-logic bug SS15 exists to eliminate. In Revision mode,
  // activeData reads the frozen snapshot map, also already fully resolved.
  function resourceEac(res) { return activeData.eac(res.code) }
  function resourceCommit(res) { return activeData.commitment(res.code) }
  function resourceActual(res) { return activeData.actual(res.code) }
  function resourceBudget(res) { return res.budget || 0 }

  function ccBudget(cc) { return cc.budget }
  function ccCommit(cc) {
    const fromRes = cc.resources.reduce((s,r) => s+resourceCommit(r), 0)
    const fromOrphans = cc.orphanSubs.reduce((s,sub) => s + activeData.commitment(sub.code), 0)
    return fromRes + fromOrphans
  }
  function ccActual(cc) {
    const fromRes = cc.resources.reduce((s,r) => s+resourceActual(r), 0)
    const fromOrphans = cc.orphanSubs.reduce((s,sub) => s + activeData.actual(sub.code), 0)
    return fromRes + fromOrphans
  }
  function ccForecast(cc) {
    const fromRes = cc.resources.reduce((s,r) => s+resourceEac(r), 0)
    const fromOrphans = cc.orphanSubs.reduce((s,sub) => s + activeData.eac(sub.code), 0)
    return fromRes + fromOrphans
  }

  const gtBudget  = tree.reduce((s,cc)=>s+ccBudget(cc),0)
  const gtCommit  = tree.reduce((s,cc)=>s+ccCommit(cc),0)
  const gtActual  = tree.reduce((s,cc)=>s+ccActual(cc),0)
  const gtForecast = tree.reduce((s,cc) => s + ccForecast(cc), 0)
  const gtEtc = gtForecast - gtActual

  async function handleSaveDraft() {
    if (!hasUnsavedEdits) return
    setBusy(true)
    try {
      const merged = { ...draftPersistedRows, ...draftEdits }
      // Ownership normalization (ADD_Forecast_Architecture.md SS16) — runs
      // exactly once, here, at the moment the Draft becomes a coherent
      // persisted state. Editing may transiently hold conflicting
      // decisions (e.g. a Resource decision and one of its Sub Code's
      // decisions at the same time); this is the single point where that
      // is resolved before anything is persisted. resolveDraft() and
      // flattenForSnapshot() both assume their input is already
      // normalized — neither contains conflict-resolution logic.
      const normalizedMerged = normalizeDecisions(merged, forecastStructure)
      const rows = await flattenForSnapshot(projectId, 'direct', normalizedMerged, baselineRows)
      await saveDraft(draftId, rows)
      setDraftPersistedRows(normalizedMerged)
      setDraftEdits({})
      alert('Draft saved.')
    } catch (e) {
      alert('Failed to save draft: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  async function handleCommit(notes) {
    setBusy(true)
    const t = () => new Date().toISOString()
    // Correlation ID — one per Commit attempt, threaded through every layer
    // (React logs → saveDraft RPC → commit_forecast_draft RPC → Postgres
    // RAISE LOG). This is what lets us tell apart "one Commit produced
    // multiple RPC executions" from "two separate Commit attempts
    // overlapped" from "the duplication happened inside one single
    // execution" — every log line for one true attempt shares one id,
    // full stop.
    const correlationId = crypto.randomUUID()
    try {
      console.log(`[COMMIT TRACE cid=${correlationId}] 1. flattenForSnapshot() starting — draftId=${draftId} at ${t()}`)
      const merged = { ...draftPersistedRows, ...draftEdits }
      // Ownership normalization (ADD_Forecast_Architecture.md SS16) — same
      // single normalization point as handleSaveDraft(), applied here too
      // since Commit can be invoked directly on unsaved edits.
      const normalizedMerged = normalizeDecisions(merged, forecastStructure)
      const rows = await flattenForSnapshot(projectId, 'direct', normalizedMerged, baselineRows)
      console.log(`[COMMIT TRACE cid=${correlationId}] 1b. flattenForSnapshot() finished — draftId=${draftId} rowCount=${rows.length} at ${t()}`)

      console.log(`[COMMIT TRACE cid=${correlationId}] 2. saveDraft() starting — draftId=${draftId} rowCount=${rows.length} at ${t()}`)
      await saveDraft(draftId, rows, correlationId)
      console.log(`[COMMIT TRACE cid=${correlationId}] 3. saveDraft() finished — draftId=${draftId} at ${t()}`)

      console.log(`[COMMIT TRACE cid=${correlationId}] 4. commitForecastDraft() starting — draftId=${draftId} at ${t()}`)
      const newSnapshotId = await commitForecastDraft(draftId, notes || null, correlationId)
      console.log(`[COMMIT TRACE cid=${correlationId}] 4b. commitForecastDraft() finished — draftId=${draftId} newSnapshotId=${newSnapshotId} at ${t()}`)

      setShowCommitModal(false)
      await load()
      setSelectedVersion('draft')
      alert('Forecast committed as a new Revision.')
    } catch (e) {
      console.log(`[COMMIT TRACE cid=${correlationId}] ERROR — draftId=${draftId} at ${t()}:`, e)
      alert('Failed to commit: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  async function handleDiscardDraftEdits() {
    if (!window.confirm(`Discard ${Object.keys(draftEdits).length} unsaved change(s) in this session? (Already-saved Draft content is not affected.)`)) return
    setDraftEdits({})
  }

  async function handleDiscardEntireDraft() {
    if (!window.confirm('Discard the ENTIRE Draft, including any saved content? This cannot be undone.')) return
    setBusy(true)
    try {
      await discardDraft(draftId)
      await load()
    } catch (e) {
      alert('Failed to discard draft: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  // ─── Create Revision — Option C conflict handling ──────────────────────
  // (ADD_Forecast_Architecture.md SS13 Invariant #1 / agreed UI behavior.)
  // Before calling create_revision_from(), check whether an active Draft
  // already exists for this project+source. If so, present the explicit
  // choice (Continue / Discard-and-create / Cancel) instead of letting the
  // RPC's rejection surface as a raw error. The SQL function itself is
  // unchanged — it still enforces "at most one Draft" unconditionally;
  // this is purely a UI-side pre-check to avoid ever calling it in a state
  // we already know it will reject, and to make the recovery path a
  // deliberate user choice rather than a manual database fix.
  const [pendingRevisionParams, setPendingRevisionParams] = useState(null) // { month, year } once user has resolved any conflict

  async function checkExistingDraftAndOpenRevisionModal() {
    // draftId/draftForecastDate are already loaded by load() on mount —
    // getOrCreateDraft() always returns/creates the current Draft, so if
    // draftId is set, an active Draft exists for this project+source.
    setShowRevisionModal(true)
  }

  async function handleCreateRevision(month, year) {
    setBusy(true)
    try {
      await createRevisionFrom(projectId, 'direct', selectedVersion, month, year)
      setShowRevisionModal(false)
      setPendingRevisionParams(null)
      await load()
      setSelectedVersion('draft')
      alert('New Draft created from that Revision.')
    } catch (e) {
      // If the SQL function rejected because a Draft already exists (the
      // exact race this modal is meant to prevent — e.g. the Draft was
      // created by another tab/session between this screen's load() and
      // the user clicking Create Revision), fall back to the explicit
      // conflict choice rather than showing a raw error.
      const msg = e.message || String(e)
      if (msg.includes('A Draft already exists')) {
        setPendingRevisionParams({ month, year })
      } else {
        alert('Failed to create revision: ' + msg)
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleContinueExistingDraft() {
    setShowRevisionModal(false)
    setPendingRevisionParams(null)
    setSelectedVersion('draft')
  }

  async function handleDiscardAndCreateRevision() {
    if (!pendingRevisionParams) return
    if (!window.confirm('Discard the current Draft (including any saved content) and create a new Draft from the selected Revision? This cannot be undone.')) return
    setBusy(true)
    try {
      await discardDraft(draftId)
      await createRevisionFrom(projectId, 'direct', selectedVersion, pendingRevisionParams.month, pendingRevisionParams.year)
      setShowRevisionModal(false)
      setPendingRevisionParams(null)
      await load()
      setSelectedVersion('draft')
      alert('Previous Draft discarded. New Draft created from that Revision.')
    } catch (e) {
      alert('Failed to discard draft and create revision: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  if (!projectId) return null

  const currentSourceLabel = viewingDraft
    ? (draftForecastDate ? `Draft (${monthYearLabel(draftForecastDate)})` : 'Draft')
    : (selectedRevision ? `${monthYearLabel(selectedRevision.forecast_date)} Rev.${selectedRevision.revision_number}` : '')

  return (
    <div className="p-4 flex flex-col gap-4">

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1C1A18]">Direct Forecast</h2>
        <button onClick={load} disabled={loading}
          className="px-4 py-2 bg-[#1C1A18] text-white text-sm rounded-lg hover:bg-[#2E2C29] disabled:opacity-50">
          {loading ? '...' : '🔄 Refresh'}
        </button>
      </div>

      <div className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Forecast Version</label>
          <select value={selectedVersion} onChange={e => setSelectedVersion(e.target.value)}
            className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm font-medium min-w-[220px]">
            <option value="draft">✏️ {draftForecastDate ? `Draft (${monthYearLabel(draftForecastDate)})` : 'Draft'}</option>
            {revisions.length > 0 && <option disabled>──────────</option>}
            {revisions.map(r => (
              <option key={r.id} value={r.id}>
                🔒 {monthYearLabel(r.forecast_date)} Rev.{r.revision_number}{r.isCurrent ? ' (Current)' : ''}
              </option>
            ))}
          </select>
        </div>

        {viewingDraft ? (
          <div className="flex items-center gap-2 flex-wrap">
            {hasUnsavedEdits && (
              <span className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                <span className="text-amber-500">⚠</span> {Object.keys(draftEdits).length} unsaved change{Object.keys(draftEdits).length>1?'s':''}
              </span>
            )}
            <button onClick={handleDiscardDraftEdits} disabled={busy || !hasUnsavedEdits || !can('Forecast.Save')}
              className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50 disabled:opacity-40">
              Discard Edits
            </button>
            <button onClick={handleDiscardEntireDraft} disabled={busy || !can('Forecast.Save')}
              className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50 disabled:opacity-40">
              Discard Draft
            </button>
            <button onClick={handleSaveDraft} disabled={busy || !hasUnsavedEdits || !can('Forecast.Save')}
              className="px-3 py-1.5 border border-amber-300 text-amber-700 rounded-lg text-xs font-bold hover:bg-amber-50 disabled:opacity-40">
              💾 Save Draft
            </button>
            {can('Forecast.Commit') && (
              <button onClick={() => setShowCommitModal(true)} disabled={busy}
                className="px-4 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 disabled:opacity-50">
                🔒 Commit
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#9CA3AF]">🔒 Read-only — committed{selectedRevision?.notes ? `: "${selectedRevision.notes}"` : ''}</span>
            {can('Forecast.Save') && (
              <button onClick={() => setShowRevisionModal(true)} disabled={busy}
                className="px-4 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 disabled:opacity-50">
                ✏️ Create Revision
              </button>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl overflow-hidden border-2 border-[#1C1A18] bg-[#1C1A18]">
        <div className="px-5 py-2 flex items-center justify-between">
          <span className="text-white font-bold text-sm tracking-wide">DIRECT FORECAST — GRAND TOTAL</span>
          <span className="text-amber-400 text-xs font-semibold">{viewingDraft ? '✏️' : '🔒'} {currentSourceLabel}</span>
        </div>
        <div className="grid grid-cols-6 bg-[#2E2C29]">
          {[
            {label:'BUDGET',value:gtBudget,color:'text-white'},
            {label:'COMMITMENT',value:gtCommit,color:'text-[#1A7A4A]'},
            {label:'ACTUAL',value:gtActual,color:'text-[#1A7A4A]'},
            {label:'ETC',value:gtEtc,color:'text-[#9CA3AF]'},
            {label:'FORECAST (EAC)',value:gtForecast,color:'text-amber-400'},
            {label:'VARIANCE',value:gtBudget-gtForecast,color:gtBudget-gtForecast>=0?'text-emerald-400':'text-red-400'},
          ].map(s => (
            <div key={s.label} className="px-5 py-4 border-r border-[#1C1A18] last:border-0">
              <p className="text-xs text-[#9CA3AF] font-semibold tracking-wider mb-1">{s.label}</p>
              <p className={`text-xl font-black ${s.color}`}>{fmt(s.value)}</p>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#9CA3AF] text-sm">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F5F1EA] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-4 py-2.5">COST CODE / RESOURCE / SUB CODE</th>
                <th className="text-left px-3 py-2.5">DESCRIPTION</th>
                <th className="text-right px-3 py-2.5">BUDGET</th>
                <th className="text-right px-3 py-2.5 text-[#1A7A4A]">COMMITMENT</th>
                <th className="text-right px-3 py-2.5 text-[#1A7A4A]">ACTUAL</th>
                <th className="text-right px-3 py-2.5 text-[#6B7280]">ETC</th>
                <th className="text-right px-3 py-2.5 text-amber-600">FORECAST EAC</th>
                <th className="text-right px-3 py-2.5 text-[#6B7280]">VARIANCE</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {tree.map((cc, idx) => {
                const isExpanded = expandedCC.has(cc.code)
                const commitVal   = ccCommit(cc)
                const actualVal   = ccActual(cc)
                const forecastVal = ccForecast(cc)
                const etcVal      = forecastVal - actualVal
                const variance    = cc.budget - forecastVal
                const bgClass     = idx%2===0?'bg-white':'bg-[#FAFAFA]'
                const hasChildren = cc.resources.length > 0 || cc.orphanSubs.length > 0

                return [
                  <tr key={cc.code} className={`border-b border-[#F3F4F6] ${bgClass} cursor-pointer hover:bg-[#F5F1EA]`}
                    onClick={() => hasChildren && toggleCC(cc.code)}>
                    <td className="px-4 py-2.5 font-mono font-bold text-[#1A7A4A] flex items-center gap-1">
                      {hasChildren && <span className="text-[#9CA3AF] text-xs">{isExpanded?'▼':'▶'}</span>}
                      {cc.code}
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-[#1C1A18]">{cc.description}</td>
                    <td className="px-3 py-2.5 text-right font-bold font-mono">{fmt(cc.budget)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[#1A7A4A]">{fmt(commitVal)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[#1A7A4A]">{fmt(actualVal)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[#6B7280]">{fmt(etcVal)}</td>
                    <td className="px-3 py-2.5 text-right font-bold font-mono text-amber-600">{forecastVal>0?fmt(forecastVal):'—'}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${variance>=0?'text-emerald-600':'text-red-500'}`}>{fmt(variance)}</td>
                    <td className="px-3 py-2.5 text-center"></td>
                  </tr>,

                  ...(isExpanded ? cc.resources.map((res) => {
                    const resExpanded = expandedRes.has(res.code)
                    const draftOnRes  = viewingDraft && draftEdits[res.code] != null
                    const hasDirectOverride = activeData.hasEntry(res.code)
                    const resCommit   = resourceCommit(res)
                    const resActual   = resourceActual(res)
                    const resForecast = resourceEac(res)
                    const resEtc      = resForecast - resActual
                    const resVariance = res.budget - resForecast
                    return [
                      <tr key={res.code} className="border-b border-[#F3F4F6] bg-[rgba(124,58,237,.06)] cursor-pointer hover:bg-[rgba(124,58,237,.1)]"
                        onClick={() => res.subs.length && toggleRes(res.code)}>
                        <td className="px-4 py-2 pl-8 font-mono text-xs text-[#7C3AED] font-semibold flex items-center gap-1">
                          {res.subs.length > 0 && <span className="text-[#9CA3AF]">{resExpanded?'▼':'▶'}</span>}
                          <span className="inline-block text-[9px] font-bold tracking-wide text-[#7C3AED] bg-[rgba(124,58,237,.12)] rounded px-1">RES</span>
                          {res.code}
                          {draftOnRes && <span className="text-[9px] text-amber-600 font-bold ml-1" title="Unsaved change this session">●unsaved</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#7C3AED] font-medium">{res.description}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs font-bold text-[#7C3AED]">{fmt(res.budget)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#1A7A4A]">{resCommit>0?fmt(resCommit):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#1A7A4A]">{resActual>0?fmt(resActual):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#6B7280]">{resEtc!==0?fmt(resEtc):'—'}</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-xs text-amber-600">
                          {resForecast>0?fmt(resForecast):'—'}
                          {hasDirectOverride && <span className="ml-1 text-[9px] text-[#7C3AED]" title="Manual forecast set directly on this Resource Code — Sub Code totals below are not summed">●</span>}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${resVariance>=0?'text-emerald-600':'text-red-500'}`}>{fmt(resVariance)}</td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={e => { e.stopPropagation(); setForecastModal({
                            cost_code: cc.code, sub_code: null, resource_code: res.code, description: res.description,
                            budget: res.budget, commitment: resCommit, actual: resActual,
                            savedEac: viewingDraft ? draftPersistedRows[res.code] : null, source: 'direct', level: 'resource',
                          })}} className="px-2 py-1 text-xs bg-amber-50 border border-amber-200 text-amber-600 rounded hover:bg-amber-100">
                            EAC
                          </button>
                        </td>
                      </tr>,

                      ...(resExpanded ? res.subs.map(sub => {
                        const subCommit   = activeData.commitment(sub.code)
                        const subActual   = activeData.actual(sub.code)
                        const draftOnSub  = viewingDraft && draftEdits[sub.code] != null
                        const subForecast = activeData.eac(sub.code)
                        const subEtc      = subForecast - subActual
                        return (
                          <tr key={sub.code} className="border-b border-[#F3F4F6] bg-[#FFFDF5]">
                            <td className="px-4 py-2 pl-14 font-mono text-xs text-[#6B7280]">
                              ↳ {sub.code}
                              {draftOnSub && <span className="text-[9px] text-amber-600 font-bold ml-1" title="Unsaved change this session">●unsaved</span>}
                            </td>
                            <td className="px-3 py-2 text-xs text-[#6B7280]">{sub.description}</td>
                            <td className="px-3 py-2 text-right text-xs text-[#9CA3AF] italic">—</td>
                            <td className="px-3 py-2 text-right font-mono text-xs text-[#1A7A4A]">{subCommit>0?fmt(subCommit):'—'}</td>
                            <td className="px-3 py-2 text-right font-mono text-xs text-[#1A7A4A]">{subActual>0?fmt(subActual):'—'}</td>
                            <td className="px-3 py-2 text-right font-mono text-xs text-[#6B7280]">{subEtc!==0?fmt(subEtc):'—'}</td>
                            <td className="px-3 py-2 text-right font-bold font-mono text-xs text-amber-600">{subForecast>0?fmt(subForecast):'—'}</td>
                            <td className="px-3 py-2 text-right font-mono text-xs text-[#9CA3AF]">—</td>
                            <td className="px-3 py-2 text-center">
                              <button onClick={() => setForecastModal({
                                cost_code: cc.code, sub_code: sub.code, resource_code: res.code, description: sub.description,
                                commitment: subCommit, actual: subActual,
                                savedEac: viewingDraft ? draftPersistedRows[sub.code] : null, source: 'direct', level: 'sub',
                              })} className="px-2 py-1 text-xs bg-amber-50 border border-amber-200 text-amber-600 rounded hover:bg-amber-100">
                                EAC
                              </button>
                            </td>
                          </tr>
                        )
                      }) : [])
                    ]
                  }).flat() : []),

                  ...(isExpanded ? cc.orphanSubs.map(sub => {
                    const subCommit   = activeData.commitment(sub.code)
                    const subActual   = activeData.actual(sub.code)
                    const draftOnSub  = viewingDraft && draftEdits[sub.code] != null
                    const subForecast = activeData.eac(sub.code)
                    const subEtc      = subForecast - subActual
                    return (
                      <tr key={sub.code} className="border-b border-[#F3F4F6] bg-[#FFFBEB]">
                        <td className="px-4 py-2 pl-8 font-mono text-xs text-[#D97706]" title="Not yet linked to a Resource Code">
                          ⚠ {sub.code}
                          {draftOnSub && <span className="text-[9px] text-amber-600 font-bold ml-1" title="Unsaved change this session">●unsaved</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#6B7280]">{sub.description}</td>
                        <td className="px-3 py-2 text-right text-xs text-[#9CA3AF] italic">—</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#1A7A4A]">{subCommit>0?fmt(subCommit):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#1A7A4A]">{subActual>0?fmt(subActual):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#6B7280]">{subEtc!==0?fmt(subEtc):'—'}</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-xs text-amber-600">{subForecast>0?fmt(subForecast):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#9CA3AF]">—</td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => setForecastModal({
                            cost_code: cc.code, sub_code: sub.code, description: sub.description,
                            commitment: subCommit, actual: subActual,
                            savedEac: viewingDraft ? draftPersistedRows[sub.code] : null, source: 'direct', level: 'sub',
                          })} className="px-2 py-1 text-xs bg-amber-50 border border-amber-200 text-amber-600 rounded hover:bg-amber-100">
                            EAC
                          </button>
                        </td>
                      </tr>
                    )
                  }) : [])
                ]
              })}
            </tbody>
            <tfoot className="bg-[#1C1A18]">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-xs font-bold text-slate-400 uppercase">Grand Total</td>
                <td className="px-3 py-3 text-right font-bold text-white font-mono">{fmt(gtBudget)}</td>
                <td className="px-3 py-3 text-right font-bold text-[#1A7A4A] font-mono">{fmt(gtCommit)}</td>
                <td className="px-3 py-3 text-right font-bold text-[#1A7A4A] font-mono">{fmt(gtActual)}</td>
                <td className="px-3 py-3 text-right font-bold text-slate-400 font-mono">{fmt(gtEtc)}</td>
                <td className="px-3 py-3 text-right font-bold text-amber-400 font-mono">{fmt(gtForecast)}</td>
                <td className={`px-3 py-3 text-right font-bold font-mono ${gtBudget-gtForecast>=0?'text-emerald-400':'text-red-400'}`}>{fmt(gtBudget-gtForecast)}</td>
                <td/>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {forecastModal && (
        <ForecastModal
          row={forecastModal}
          draft={draftEdits[forecastModal.sub_code || forecastModal.resource_code || forecastModal.cost_code]}
          readOnly={!viewingDraft}
          onClose={() => setForecastModal(null)}
          onDraftChange={handleDraftChange}
          onClearDraft={handleClearDraft}
        />
      )}

      {showCommitModal && (
        <CommitModal
          periodLabel={draftForecastDate ? monthYearLabel(draftForecastDate) : ''}
          committing={busy}
          onClose={() => setShowCommitModal(false)}
          onConfirm={handleCommit}
        />
      )}

      {showRevisionModal && (
        <CreateRevisionModal
          sourceLabel={currentSourceLabel}
          existingDraftInfo={pendingRevisionParams ? { forecast_date: draftForecastDate } : null}
          creating={busy}
          onClose={() => { setShowRevisionModal(false); setPendingRevisionParams(null) }}
          onConfirm={handleCreateRevision}
          onContinueExistingDraft={handleContinueExistingDraft}
          onDiscardAndCreate={handleDiscardAndCreateRevision}
        />
      )}
    </div>
  )
}
